// Locations module for Docospot
// Handles location management with infinite scroll, filtering, and location selection

import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { API_BASE_URL } from '../core/config.js';
import { showError, showSuccess } from '../core/ui.js';
import { updatePageTitle, updateActiveNavigation } from './navigation.js';

// Import location CRUD operations
import { 
    editLocation, 
    handleUpdateLocation, 
    closeEditLocationModal,
    toggleLocationAccountTreeItem,
    selectLocationAccount,
    geocodeLocationAddress 
} from './location-edit.js';

// Infinite scroll state
export const locationsInfiniteScroll = {
    currentPage: 1,
    pageSize: 50,
    allLocations: [],
    totalCount: 0,
    hasMore: true,
    isLoading: false,
    searchQuery: '',
    filterAccountIds: [],
    searchTimeout: null,
    sortBy: 'name',
    sortOrder: 'asc',
    requestSeq: 0
};

// Location selection state for mass operations
export const locationSelection = {
    selectedLocationIds: new Set(),
    selectAll: false
};

/**
 * Display the locations page
 */
export async function showLocations() {
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    if (!auth.isAuthenticated()) {
        showError('Please login to view locations');
        return;
    }

    mainContent.innerHTML = `
        <!-- Enhanced Filter Row - Sticky -->
        <div class="sticky top-16 z-30 mb-0 bg-gray-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3">
            <div class="bg-gray-200 shadow rounded-lg p-4">
                <!-- Row 1: Add Location Button -->
                <div class="flex justify-end mb-2">
                    <button onclick="showCreateLocationModal()" 
                            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 shadow-sm">
                        <svg class="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        ${t('locations.addButton')}
                    </button>
                </div>
                
                <!-- Row 2: Filters and Action Buttons -->
                <div class="flex items-end justify-between gap-4">
                    <!-- Left side: Search and Account Filter -->
                    <div class="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Search Field -->
                        <div>
                            <label for="location-search" class="block text-sm font-medium text-gray-700 mb-1">${t('locations.search')}</label>
                            <input type="text" id="location-search" placeholder="${t('locations.searchPlaceholder')}" 
                                   class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                                   oninput="filterLocations()">
                        </div>
                        
                        <!-- Account Filter -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">${t('locations.filterByAccount')}</label>
                            <div class="relative">
                                <button type="button" id="locations-account-filter-toggle" 
                                        onclick="toggleLocationsAccountFilterExpanded()"
                                        class="w-full text-left border border-gray-300 rounded-md px-3 py-2 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                                    <div class="flex items-center justify-between">
                                        <span id="locations-account-filter-display" class="text-sm text-gray-700">${t('locations.anyAccount')}</span>
                                        <svg id="locations-account-filter-arrow" class="w-4 h-4 text-gray-400 transform transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                        </svg>
                                    </div>
                                </button>
                                <div id="locations-account-filter-tree-container" class="hidden absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                                    <div id="locations-account-filter-tree" class="p-2 max-h-64 overflow-y-auto">
                                        <div class="text-gray-400 text-sm">${t('locations.loadingAccounts')}</div>
                                    </div>
                                    <div class="border-t border-gray-200 px-3 py-2 bg-gray-50 flex items-center justify-end">
                                        <button type="button" onclick="clearLocationsAccountFilter()" class="text-sm text-gray-600 hover:text-gray-700">
                                            ${t('locations.clearAll')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Right side: Action buttons -->
                    <div class="flex items-end space-x-2">
                        <button onclick="clearLocationsFilters()" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            ${t('locations.clear')}
                        </button>
                        <button onclick="loadLocationsData(true)" 
                                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                            <svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            ${t('locations.refresh')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Locations Count Display -->
        <div class="bg-white shadow rounded-lg mt-3 px-6 py-3">
            <div id="locations-count-display" class="text-sm text-gray-600">
                <span class="font-medium">Loading locations...</span>
            </div>
        </div>
        
        <!-- Locations List -->
        <div class="bg-white shadow rounded-lg mt-3">
            <div id="locations-list" class="space-y-4">
                <div class="text-center py-4">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p class="mt-2 text-sm text-gray-500">Loading locations...</p>
                </div>
            </div>
        </div>
        
        <!-- Loading indicator for infinite scroll -->
        <div id="locations-loading-more" class="hidden mt-4 text-center py-4 bg-white shadow rounded-lg">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
            <p class="mt-2 text-xs text-gray-500">Loading more locations...</p>
        </div>

        <!-- End of List Message -->
        <div id="locations-end-message" class="hidden mt-4 text-center py-4 bg-white shadow rounded-lg text-sm text-gray-500">
            All locations loaded
        </div>
    `;

    updatePageTitle('Setup - Locations');
    updateActiveNavigation('locations');

    await loadLocationsAccountFilter();

    locationsInfiniteScroll.currentPage = 1;
    locationsInfiniteScroll.allLocations = [];
    locationsInfiniteScroll.hasMore = true;
    locationsInfiniteScroll.isLoading = false;

    await loadLocationsData(true);
    setupLocationsInfiniteScroll();
}

/**
 * Setup infinite scroll listener
 */
function setupLocationsInfiniteScroll() {
    let scrollTimeout;
    
    window.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
            const scrollPosition = window.innerHeight + window.scrollY;
            const documentHeight = document.documentElement.scrollHeight;
            const threshold = 300;
            
            if (scrollPosition >= documentHeight - threshold) {
                if (locationsInfiniteScroll.hasMore && !locationsInfiniteScroll.isLoading) {
                    loadLocationsData(false);
                }
            }
        }, 100);
    });
}

/**
 * Load account filter tree
 */
async function loadLocationsAccountFilter() {
    try {
        const response = await fetch(`${API_BASE_URL}/accounts/hierarchy`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`
            }
        });

        if (!response.ok) throw new Error('Failed to load accounts');
        
        const data = await response.json();
        const container = document.getElementById('locations-account-filter-tree');
        if (!container) return;

        if (data && data.accounts_tree) {
            container.innerHTML = renderLocationsAccountFilterTree(data.accounts_tree);
        } else {
            container.innerHTML = '<div class="text-gray-500 text-sm">No accounts found</div>';
        }
            
    } catch (error) {
        console.error('Error loading accounts for filter:', error);
        const container = document.getElementById('locations-account-filter-tree');
        if (container) {
            container.innerHTML = '<div class="text-red-500 text-sm">Error loading accounts</div>';
        }
    }
}

/**
 * Render account filter tree
 */
function renderLocationsAccountFilterTree(accounts, level = 0) {
    let html = '';
    
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const accountName = account.account_name || account.name || 'Unnamed Account';
        const hasChildren = account.children && account.children.length > 0;
        const indentPadding = level * 20;
        // Auto-expand the top level so users see sub-accounts without an extra
        // click, matching the account pickers elsewhere (e.g. Time Budget).
        const expandByDefault = level === 0;
        
        html += `
            <div class="account-tree-item" data-level="${level}">
                <div class="flex items-center py-1.5 hover:bg-gray-50 rounded" style="padding-left: ${indentPadding}px;">
                    ${hasChildren ? `
                        <button type="button" onclick="toggleLocationsAccountFilterTreeItem('locations-filter-tree-${accountId}')" 
                                class="mr-1 text-gray-400 hover:text-gray-600 flex-shrink-0 w-4 h-4 flex items-center justify-center">
                            <svg id="locations-filter-tree-icon-${accountId}" class="w-4 h-4 transform transition-transform${expandByDefault ? ' rotate-90' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    ` : '<span class="mr-1 w-4 h-4 flex-shrink-0"></span>'}
                    <label class="flex items-center cursor-pointer flex-1">
                        <input type="checkbox" value="${accountId}" 
                               onchange="applyLocationsAccountFilter()"
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded">
                        <span class="ml-2 text-sm text-gray-700">${accountName}</span>
                    </label>
                </div>
                ${hasChildren ? `
                    <div id="locations-filter-tree-${accountId}"${expandByDefault ? '' : ' class="hidden"'}>
                        ${renderLocationsAccountFilterTree(account.children, level + 1)}
                    </div>
                ` : ''}
            </div>
        `;
    });
    
    return html;
}

/**
 * Toggle account filter tree item
 */
export function toggleLocationsAccountFilterTreeItem(itemId) {
    const item = document.getElementById(itemId);
    const iconId = itemId.replace('locations-filter-tree-', 'locations-filter-tree-icon-');
    const icon = document.getElementById(iconId);
    
    if (item && icon) {
        item.classList.toggle('hidden');
        icon.classList.toggle('rotate-90');
    }
}

/**
 * Filter locations (debounced)
 */
export function filterLocations() {
    if (locationsInfiniteScroll.searchTimeout) {
        clearTimeout(locationsInfiniteScroll.searchTimeout);
    }
    
    locationsInfiniteScroll.searchTimeout = setTimeout(() => {
        const searchTerm = document.getElementById('location-search')?.value?.trim() || '';
        locationsInfiniteScroll.searchQuery = searchTerm;
        locationsInfiniteScroll.currentPage = 1;
        locationsInfiniteScroll.allLocations = [];
        locationsInfiniteScroll.hasMore = true;
        loadLocationsData(true);
    }, 300);
}

/**
 * Toggle account filter dropdown
 */
export function toggleLocationsAccountFilterExpanded() {
    const container = document.getElementById('locations-account-filter-tree-container');
    const arrow = document.getElementById('locations-account-filter-arrow');
    
    if (container && arrow) {
        const isHidden = container.classList.contains('hidden');
        container.classList.toggle('hidden');
        arrow.classList.toggle('rotate-180');
        
        if (isHidden) {
            // Opening - add click-outside listener
            setTimeout(() => {
                document.addEventListener('click', closeLocationsFilterOnClickOutside);
            }, 0);
        } else {
            // Closing - remove listener
            document.removeEventListener('click', closeLocationsFilterOnClickOutside);
        }
    }
}

function closeLocationsFilterOnClickOutside(event) {
    const container = document.getElementById('locations-account-filter-tree-container');
    const button = event.target.closest('[onclick*="toggleLocationsAccountFilterExpanded"]');
    
    if (container && !container.contains(event.target) && !button) {
        container.classList.add('hidden');
        const arrow = document.getElementById('locations-account-filter-arrow');
        if (arrow) arrow.classList.remove('rotate-180');
        document.removeEventListener('click', closeLocationsFilterOnClickOutside);
    }
}

/**
 * Apply account filter
 */
export function applyLocationsAccountFilter() {
    const checkboxes = document.querySelectorAll('#locations-account-filter-tree input[type="checkbox"]:checked');
    const display = document.getElementById('locations-account-filter-display');
    
    if (display) {
        if (checkboxes.length === 0) {
            display.textContent = 'Any account';
        } else if (checkboxes.length === 1) {
            const label = checkboxes[0].closest('label')?.textContent?.trim();
            display.textContent = label || '1 account';
        } else {
            display.textContent = `${checkboxes.length} accounts`;
        }
    }
    
    locationsInfiniteScroll.filterAccountIds = Array.from(checkboxes).map(cb => cb.value);
    locationsInfiniteScroll.currentPage = 1;
    locationsInfiniteScroll.allLocations = [];
    locationsInfiniteScroll.hasMore = true;
    loadLocationsData(true);
}

/**
 * Clear account filter
 */
export function clearLocationsAccountFilter() {
    const checkboxes = document.querySelectorAll('#locations-account-filter-tree input[type="checkbox"]');
    checkboxes.forEach(cb => cb.checked = false);
    
    const display = document.getElementById('locations-account-filter-display');
    if (display) display.textContent = 'Any account';
    
    locationsInfiniteScroll.filterAccountIds = [];
    locationsInfiniteScroll.currentPage = 1;
    locationsInfiniteScroll.allLocations = [];
    locationsInfiniteScroll.hasMore = true;
    loadLocationsData(true);
}

/**
 * Clear all filters
 */
export function clearLocationsFilters() {
    const searchInput = document.getElementById('location-search');
    if (searchInput) searchInput.value = '';
    
    clearLocationsAccountFilter();
}

/**
 * Load locations data with pagination
 */
export async function loadLocationsData(isInitialLoad = false) {
    // Incremental (scroll) loads wait for any in-flight load and stop when there
    // is nothing more. Fresh loads (initial / sort / filter / search) always
    // proceed and supersede any in-flight request, so a stale page can never
    // overwrite the freshly reset list.
    if (!isInitialLoad && (locationsInfiniteScroll.isLoading || !locationsInfiniteScroll.hasMore)) {
        return;
    }

    if (isInitialLoad) {
        locationsInfiniteScroll.requestSeq = (locationsInfiniteScroll.requestSeq || 0) + 1;
        locationsInfiniteScroll.currentPage = 1;
        locationsInfiniteScroll.allLocations = [];
        locationsInfiniteScroll.hasMore = true;
    }
    const seq = locationsInfiniteScroll.requestSeq || 0;

    locationsInfiniteScroll.isLoading = true;

    if (!isInitialLoad) {
        const loadingMore = document.getElementById('locations-loading-more');
        if (loadingMore) loadingMore.classList.remove('hidden');
    }

    try {
        const skip = (locationsInfiniteScroll.currentPage - 1) * locationsInfiniteScroll.pageSize;
        const params = new URLSearchParams({
            skip: skip.toString(),
            limit: locationsInfiniteScroll.pageSize.toString()
        });

        if (locationsInfiniteScroll.filterAccountIds.length > 0) {
            locationsInfiniteScroll.filterAccountIds.forEach(id => {
                params.append('account_id', id);
            });
        }

        if (locationsInfiniteScroll.searchQuery) {
            params.append('search', locationsInfiniteScroll.searchQuery);
        }

        params.append('sort_by', locationsInfiniteScroll.sortBy);
        params.append('sort_order', locationsInfiniteScroll.sortOrder);

        const response = await fetch(`${API_BASE_URL}/locations/?${params}`, {
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`
            }
        });

        if (!response.ok) {
            throw new Error('Failed to load locations');
        }

        const data = await response.json();

        // A newer fresh load (sort / filter / search / initial) superseded this
        // request while it was in flight — discard its results so pages from an
        // old order can't pollute the list.
        if (seq !== (locationsInfiniteScroll.requestSeq || 0)) {
            return;
        }

        const newLocations = data.locations || [];
        const totalCount = data.total || 0;
        
        locationsInfiniteScroll.totalCount = totalCount;
        locationsInfiniteScroll.allLocations.push(...newLocations);

        if (locationsInfiniteScroll.allLocations.length >= totalCount || newLocations.length < locationsInfiniteScroll.pageSize) {
            locationsInfiniteScroll.hasMore = false;
        }

        const loadingMore = document.getElementById('locations-loading-more');
        if (loadingMore) loadingMore.classList.add('hidden');

        const endMessage = document.getElementById('locations-end-message');
        if (endMessage) {
            if (locationsInfiniteScroll.hasMore) {
                endMessage.classList.add('hidden');
            } else if (locationsInfiniteScroll.allLocations.length > 0) {
                endMessage.classList.remove('hidden');
            }
        }

        displayLocations(locationsInfiniteScroll.allLocations);
        updateLocationsCount();
        locationsInfiniteScroll.currentPage++;
        locationsInfiniteScroll.isLoading = false;

    } catch (error) {
        locationsInfiniteScroll.isLoading = false;
        showError('Failed to load locations: ' + (error.message || 'Unknown error'));
        console.error('Error loading locations:', error);

        const loadingMore = document.getElementById('locations-loading-more');
        if (loadingMore) loadingMore.classList.add('hidden');

        const locationsList = document.getElementById('locations-list');
        if (locationsList && locationsInfiniteScroll.allLocations.length === 0) {
            locationsList.innerHTML = `
                <div class="text-center py-8">
                    <p class="text-red-600">Error loading locations: ${error.message || 'Unknown error'}</p>
                    <button onclick="loadLocationsData(true)" class="mt-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
                        Retry
                    </button>
                </div>
            `;
        }
    }
}

/**
 * Change the locations list sort. Clicking the active column toggles the
 * direction; clicking a new column sorts it ascending. Sorting is applied
 * server-side so it stays correct across the full (paginated) result set.
 */
export function setLocationsSort(column) {
    if (locationsInfiniteScroll.sortBy === column) {
        locationsInfiniteScroll.sortOrder = locationsInfiniteScroll.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
        locationsInfiniteScroll.sortBy = column;
        locationsInfiniteScroll.sortOrder = 'asc';
    }
    locationsInfiniteScroll.currentPage = 1;
    locationsInfiniteScroll.allLocations = [];
    locationsInfiniteScroll.hasMore = true;
    loadLocationsData(true);
}

/**
 * Render a sort-direction indicator for a sortable column header.
 */
function locationSortIndicator(column) {
    const active = locationsInfiniteScroll.sortBy === column;
    if (!active) {
        return '<span class="text-gray-400 ml-1">↕</span>';
    }
    return locationsInfiniteScroll.sortOrder === 'asc'
        ? '<span class="text-gray-700 ml-1">▲</span>'
        : '<span class="text-gray-700 ml-1">▼</span>';
}

// Natural, case-insensitive comparison (numeric-aware) matching the server's
// collation, so "EGE 35" sorts before "EGE 315".
function naturalLocationCompare(a, b) {
    return String(a == null ? '' : a).localeCompare(
        String(b == null ? '' : b), 'da', { numeric: true, sensitivity: 'base' }
    );
}

// Sort key for a location based on the current sort column.
function locationSortKey(loc) {
    if (locationsInfiniteScroll.sortBy === 'sensor_id') {
        const ids = (loc.sensors || []).map(s => s && s.sensor_id).filter(Boolean);
        if (ids.length === 0) return '';
        return ids.reduce((min, id) => (naturalLocationCompare(id, min) < 0 ? id : min), ids[0]);
    }
    return loc.name || '';
}

// Guarantee the displayed list is always correctly ordered, regardless of the
// order in which paginated results arrive. The server also sorts (for correct
// pagination windows); this is a defensive client-side sort for display.
function sortLocationsForDisplay(locations) {
    const dir = locationsInfiniteScroll.sortOrder === 'desc' ? -1 : 1;
    const bySensor = locationsInfiniteScroll.sortBy === 'sensor_id';
    return locations.slice().sort((a, b) => {
        const ka = locationSortKey(a);
        const kb = locationSortKey(b);
        if (bySensor) {
            // Locations without a sensor always sort last.
            if (ka === '' && kb !== '') return 1;
            if (kb === '' && ka !== '') return -1;
        }
        const cmp = naturalLocationCompare(ka, kb);
        if (cmp !== 0) return dir * cmp;
        return dir * naturalLocationCompare(a.name || '', b.name || '');
    });
}

/**
 * Display locations in table
 */
function displayLocations(locations) {
    const locationsList = document.getElementById('locations-list');
    if (!locationsList) return;

    locations = sortLocationsForDisplay(locations);

    if (locations.length === 0) {
        locationsList.innerHTML = `
            <div class="text-center py-8">
                <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                <h3 class="mt-2 text-sm font-medium text-gray-900">No locations found</h3>
                <p class="mt-1 text-sm text-gray-500">Get started by creating a new location.</p>
                <div class="mt-6">
                    <button onclick="showCreateLocationModal()" 
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
                        <svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add Location
                    </button>
                </div>
            </div>
        `;
        return;
    }

    locationsList.innerHTML = `
        <div class="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-200">
                    <tr>
                        <th scope="col" class="px-6 py-3 bg-gray-200">
                            <input type="checkbox" 
                                   id="select-all-locations" 
                                   onchange="window.toggleSelectAllLocations(this.checked)"
                                   class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer">
                        </th>
                        <th scope="col" onclick="window.setLocationsSort('name')"
                            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-200 cursor-pointer select-none hover:bg-gray-300">
                            ${t('common.name')}${locationSortIndicator('name')}
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-200">
                            ${t('locations.account')}
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-200">
                            ${t('common.address')}
                        </th>
                        <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-200">
                            ${t('locations.timeBudget')}
                        </th>
                        <th scope="col" onclick="window.setLocationsSort('sensor_id')"
                            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-200 cursor-pointer select-none hover:bg-gray-300">
                            ${t('sensors.sensorId')}s${locationSortIndicator('sensor_id')}
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    ${generateLocationRows(locations)}
                </tbody>
            </table>
        </div>
    `;
}

/**
 * Generate location table rows
 */
function generateLocationRows(locations) {
    return locations.map(location => {
        const locationId = location.id || location._id;
        const address = location.address || 'No address specified';
        const accountName = location.account_name || 'Unknown Account';
        const sensors = location.sensors || [];

        // Time budget summary for quick scanning of which locations still miss
        // a budget. Shows e.g. "40h / Weekly" when set, otherwise a muted dash.
        let budgetDisplay = '<span class="text-gray-400">—</span>';
        if (location.time_budget_hours && location.time_budget_period) {
            const periodLabel = t(`time.${location.time_budget_period}`);
            budgetDisplay = `<span class="font-medium text-gray-900">${location.time_budget_hours}h</span>`
                + `<span class="text-gray-500"> / ${periodLabel}</span>`;
        }
        
        // Calculate alarm status for the location based on its sensors
        let locationAlarmIcon = '';
        if (sensors.length > 0) {
            // Find the sensor with the highest priority alarm status
            // Priority: suspended > active > disabled > none
            let hasSuspended = false;
            let hasActive = false;
            let hasDisabled = false;
            
            for (const sensor of sensors) {
                const alarmStatus = sensor.alarm_status;
                if (alarmStatus && alarmStatus.total_alarms > 0) {
                    if (alarmStatus.suspended_count > 0) {
                        hasSuspended = true;
                        break; // Highest priority, stop checking
                    } else if (alarmStatus.active_count > 0) {
                        hasActive = true;
                    } else if (alarmStatus.disabled_count > 0) {
                        hasDisabled = true;
                    }
                }
            }
            
            // Generate icon based on priority
            if (hasSuspended) {
                locationAlarmIcon = `
                    <svg class="w-4 h-4 text-yellow-500 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" title="Location has suspended alarm(s)">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                `;
            } else if (hasActive) {
                locationAlarmIcon = `
                    <svg class="w-4 h-4 text-green-500 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" title="Location has active alarm(s)">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                `;
            } else if (hasDisabled) {
                locationAlarmIcon = `
                    <svg class="w-4 h-4 text-gray-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" title="Location has disabled alarm(s)">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                `;
            }
        }
        
        let sensorIdsDisplay = '';
        if (sensors.length === 0) {
            sensorIdsDisplay = '<span class="text-gray-400 text-xs">None</span>';
        } else if (sensors.length <= 3) {
            sensorIdsDisplay = sensors.map(s => 
                `<code class="bg-gray-100 px-2 py-1 rounded text-xs mr-1 mb-1 inline-block">${s.sensor_id}</code>`
            ).join('');
        } else {
            sensorIdsDisplay = sensors.slice(0, 2).map(s => 
                `<code class="bg-gray-100 px-2 py-1 rounded text-xs mr-1 mb-1 inline-block">${s.sensor_id}</code>`
            ).join('');
            sensorIdsDisplay += `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 ml-1">+${sensors.length - 2} more</span>`;
        }

        // SmartTime indicator: rendered in a fixed-width slot in front of
        // every location name so it is easy to scan a long list and instantly
        // see which locations have SmartTime configured and which do not.
        // Configured: solid red clock with the user count.
        // Not configured: faded outline placeholder (keeps column alignment).
        const smartTimeCount = Array.isArray(location.smart_time_users) ? location.smart_time_users.length : 0;
        const smartTimeConfigured = smartTimeCount > 0;
        const smartTimeTitle = smartTimeConfigured
            ? (t('locations.smarttimeUserCount') || '{count} SmartTime user(s) configured').replace('{count}', smartTimeCount)
            : (t('locations.smarttimeNotConfigured') || 'No SmartTime configured');
        const smartTimeIcon = `
            <span class="inline-flex items-center justify-center w-10 mr-2 flex-shrink-0 ${smartTimeConfigured ? 'text-green-600' : 'text-gray-300'}"
                  title="${smartTimeTitle}" aria-label="${smartTimeTitle}">
                <svg class="w-4 h-4" fill="${smartTimeConfigured ? 'currentColor' : 'none'}" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                ${smartTimeConfigured ? `<span class="text-[11px] font-bold leading-none ml-0.5">${smartTimeCount}</span>` : ''}
            </span>
        `;

        return `
            <tr class="location-item hover:bg-gray-50">
                <td class="px-6 py-4">
                    <input type="checkbox" 
                           class="location-checkbox h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                           data-location-id="${locationId}"
                           ${locationSelection.selectedLocationIds.has(locationId) ? 'checked' : ''}
                           onchange="window.toggleLocationSelection('${locationId}', this.checked)">
                </td>
                <td class="px-6 py-4 text-sm">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 h-8 w-8">
                            <div class="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                                <svg class="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                                </svg>
                            </div>
                        </div>
                        <div class="ml-4 flex items-center">
                            ${smartTimeIcon}
                            ${locationAlarmIcon}
                            <button onclick="editLocation('${locationId}')" class="text-sm font-medium text-primary-600 hover:text-primary-900">${location.name}</button>
                        </div>
                    </div>
                </td>
                <td class="px-6 py-4 text-sm text-gray-900">
                    <div class="flex items-center">
                        <svg class="h-4 w-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                        </svg>
                        ${accountName}
                    </div>
                </td>
                <td class="px-6 py-4 text-sm text-gray-900">${address}</td>
                <td class="px-6 py-4 text-sm whitespace-nowrap">${budgetDisplay}</td>
                <td class="px-6 py-4 text-sm">
                    <div class="flex flex-wrap items-center">${sensorIdsDisplay}</div>
                </td>
            </tr>
        `;
    }).join('');
}

/**
 * Update locations count display
 */
function updateLocationsCount() {
    const countElement = document.getElementById('locations-count-display');
    if (!countElement) return;
    
    const displayedCount = locationsInfiniteScroll.allLocations.length;
    const totalCount = locationsInfiniteScroll.totalCount;
    
    const hasAccountFilter = locationsInfiniteScroll.filterAccountIds.length > 0;
    const hasSearch = locationsInfiniteScroll.searchQuery && locationsInfiniteScroll.searchQuery.trim() !== '';
    const hasFilters = hasAccountFilter || hasSearch;
    
    if (hasFilters) {
        let filterDescription = [];
        if (hasSearch) filterDescription.push(`search: "${locationsInfiniteScroll.searchQuery}"`);
        if (hasAccountFilter) filterDescription.push(`account filter`);
        
        countElement.innerHTML = `
            <div class="flex items-center space-x-2">
                <span class="font-medium">${t('common.showing')} ${displayedCount.toLocaleString()} ${t('common.of')} ${totalCount.toLocaleString()} ${t('locations.locations')}</span>
                <span class="text-gray-400">•</span>
                <span class="text-blue-600">${filterDescription.join(', ')}</span>
            </div>
        `;
    } else {
        const loadingMore = displayedCount < totalCount && locationsInfiniteScroll.hasMore;
        countElement.innerHTML = `
            <div class="flex items-center space-x-2">
                <span class="font-medium">${t('common.total')}: ${totalCount.toLocaleString()} ${t('locations.locations')}</span>
                ${loadingMore ? `<span class="text-gray-400">• <span class="italic">showing first ${displayedCount.toLocaleString()}, scroll for more</span></span>` : ''}
            </div>
        `;
    }
}

// ============================================================================
// Location Selection Functions for Mass Alarm Operations
// ============================================================================

function toggleLocationSelection(locationId, checked) {
    if (checked) {
        locationSelection.selectedLocationIds.add(locationId);
    } else {
        locationSelection.selectedLocationIds.delete(locationId);
        locationSelection.selectAll = false;
        const selectAllCheckbox = document.getElementById('select-all-locations');
        if (selectAllCheckbox) selectAllCheckbox.checked = false;
    }
    updateLocationSelectionUI();
}

function toggleSelectAllLocations(checked) {
    locationSelection.selectAll = checked;
    
    if (checked) {
        // Select all displayed locations
        locationsInfiniteScroll.allLocations.forEach(loc => {
            const id = loc.id || loc._id;
            locationSelection.selectedLocationIds.add(id);
        });
    } else {
        // Deselect all
        locationSelection.selectedLocationIds.clear();
    }
    
    // Update all checkboxes
    document.querySelectorAll('.location-checkbox').forEach(checkbox => {
        checkbox.checked = checked;
    });
    
    updateLocationSelectionUI();
}

function clearLocationSelection() {
    locationSelection.selectedLocationIds.clear();
    locationSelection.selectAll = false;
    
    // Uncheck all checkboxes
    const selectAllCheckbox = document.getElementById('select-all-locations');
    if (selectAllCheckbox) selectAllCheckbox.checked = false;
    
    document.querySelectorAll('.location-checkbox').forEach(checkbox => {
        checkbox.checked = false;
    });
    
    updateLocationSelectionUI();
}

function updateLocationSelectionUI() {
    const count = locationSelection.selectedLocationIds.size;
    let actionBar = document.getElementById('location-mass-action-bar');
    
    if (count > 0) {
        // Show action bar if it doesn't exist
        if (!actionBar) {
            actionBar = document.createElement('div');
            actionBar.id = 'location-mass-action-bar';
            actionBar.className = 'fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-200 rounded-lg shadow-2xl border border-gray-300 px-6 py-4 z-40 flex items-center space-x-6';
            actionBar.innerHTML = `
                <div class="flex items-center space-x-2 text-sm font-medium text-gray-700">
                    <span id="selected-count">${count}</span>
                    <span>location(s) selected</span>
                </div>
                <div class="h-6 w-px bg-gray-300"></div>
                <div class="flex items-center space-x-4">
                    <button onclick="window.showMassAssignAlarmModal()" 
                            class="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                            title="Assign alarm to selected locations">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span>Assign</span>
                    </button>
                    <button onclick="window.showMassRemoveAlarmModal()" 
                            class="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                            title="Remove alarm from selected locations">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        <span>Remove</span>
                    </button>
                    <button onclick="window.showMassSuspendAlarmModal()" 
                            class="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md transition-colors"
                            title="Suspend alarms for selected locations">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Suspend</span>
                    </button>
                    <button onclick="window.showMassResumeAlarmModal()" 
                            class="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                            title="Resume alarms for selected locations">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Resume</span>
                    </button>
                </div>
                <div class="h-6 w-px bg-gray-300"></div>
                <button onclick="window.clearLocationSelection()" 
                        class="text-sm text-gray-600 hover:text-gray-900 font-medium"
                        title="Clear selection">
                    Clear
                </button>
            `;
            document.body.appendChild(actionBar);
        } else {
            // Update count
            const countSpan = actionBar.querySelector('#selected-count');
            if (countSpan) countSpan.textContent = count;
        }
    } else {
        // Hide action bar
        if (actionBar) {
            actionBar.remove();
        }
    }
}

// ============================================================
// Mass alarm operation modals
// ============================================================

let _alarmTemplatesCache = null;

async function fetchAlarmTemplates() {
    if (_alarmTemplatesCache) return _alarmTemplatesCache;
    try {
        const response = await fetch(`${API_BASE_URL}/alarm-templates/templates`, {
            headers: { 'Authorization': `Bearer ${auth.token}` }
        });
        if (!response.ok) throw new Error('Failed to load alarm templates');
        _alarmTemplatesCache = await response.json();
        return _alarmTemplatesCache;
    } catch (e) {
        console.error('Error loading alarm templates:', e);
        showError(t('locations.massAlarm.loadTemplatesError') || 'Failed to load alarm templates');
        return [];
    }
}

function closeMassAlarmModal() {
    const modal = document.getElementById('mass-alarm-modal');
    if (modal) modal.remove();
}

function buildTemplateOptions(templates, { allowAll = false } = {}) {
    const opts = [];
    if (allowAll) {
        opts.push(`<option value="">${t('locations.massAlarm.allTemplates') || 'All alarm templates'}</option>`);
    } else {
        opts.push(`<option value="">${t('locations.massAlarm.selectTemplate') || 'Select an alarm template…'}</option>`);
    }
    for (const tpl of templates) {
        const name = tpl.template_name || tpl.name || tpl._id;
        opts.push(`<option value="${tpl._id || tpl.id}">${name}</option>`);
    }
    return opts.join('');
}

async function openMassAlarmModal({ title, description, action, templateRequired, allowAll, extraFieldsHtml = '', confirmLabel, confirmClass = 'bg-primary-600 hover:bg-primary-700', filterTemplatesBySensorTypes = false }) {
    const selectedIds = Array.from(locationSelection.selectedLocationIds);
    if (selectedIds.length === 0) {
        showError(t('locations.massAlarm.noSelection') || 'No locations selected');
        return;
    }

    let templates = await fetchAlarmTemplates();
    let sensorTypeNotice = '';
    if (filterTemplatesBySensorTypes) {
        const info = await fetchSensorTypesForLocations(selectedIds);
        const presentTypes = new Set(info.sensor_types || []);
        if (presentTypes.size === 0) {
            sensorTypeNotice = `<div class="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">${t('locations.massAlarm.noSensors') || 'The selected locations have no sensors.'}</div>`;
            templates = [];
        } else {
            templates = templates.filter(tpl => presentTypes.has(tpl.sensor_type || 'card_reader'));
            if (templates.length === 0) {
                const list = Array.from(presentTypes).join(', ');
                sensorTypeNotice = `<div class="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">${(t('locations.massAlarm.noMatchingTemplates') || 'No alarm templates match the sensor types in the selected locations ({types}).').replace('{types}', list)}</div>`;
            }
        }
    }
    closeMassAlarmModal();

    const modal = document.createElement('div');
    modal.id = 'mass-alarm-modal';
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    modal.innerHTML = `
        <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div class="px-6 py-4 border-b border-gray-200">
                <h3 class="text-lg font-semibold text-gray-900">${title}</h3>
                <p class="text-sm text-gray-600 mt-1">${description.replace('{count}', selectedIds.length)}</p>
            </div>
            <div class="px-6 py-4 space-y-4">
                <div id="mass-alarm-error" class="hidden p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700"></div>
                ${sensorTypeNotice}
                <div>
                    <label for="mass-alarm-template" class="block text-sm font-medium text-gray-700 mb-1">
                        ${t('locations.massAlarm.template') || 'Alarm template'}${templateRequired ? ' *' : ''}
                    </label>
                    <select id="mass-alarm-template" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                        ${buildTemplateOptions(templates, { allowAll })}
                    </select>
                </div>
                ${extraFieldsHtml}
            </div>
            <div class="px-6 py-3 bg-gray-50 rounded-b-lg flex justify-end space-x-2">
                <button type="button" onclick="window.closeMassAlarmModal()"
                        class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                    ${t('common.cancel') || 'Cancel'}
                </button>
                <button type="button" id="mass-alarm-submit"
                        class="px-4 py-2 text-sm font-medium text-white rounded-md ${confirmClass}">
                    ${confirmLabel}
                </button>
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    document.getElementById('mass-alarm-submit').addEventListener('click', async () => {
        const errEl = document.getElementById('mass-alarm-error');
        errEl.classList.add('hidden');
        errEl.textContent = '';

        const templateId = document.getElementById('mass-alarm-template').value || null;
        if (templateRequired && !templateId) {
            errEl.textContent = t('locations.massAlarm.templateRequired') || 'Please select an alarm template';
            errEl.classList.remove('hidden');
            return;
        }

        const submitBtn = document.getElementById('mass-alarm-submit');
        const originalLabel = submitBtn.textContent;
        submitBtn.disabled = true;
        submitBtn.textContent = t('common.processing') || 'Processing…';

        try {
            const result = await action({ locationIds: selectedIds, templateId });
            closeMassAlarmModal();
            const msg = formatMassResultMessage(result);
            showSuccess(msg);
            clearLocationSelection();
            // Refresh list if data changed
            if (typeof loadLocationsData === 'function') {
                loadLocationsData();
            }
        } catch (e) {
            errEl.textContent = e.message || (t('common.error') || 'Operation failed');
            errEl.classList.remove('hidden');
            submitBtn.disabled = false;
            submitBtn.textContent = originalLabel;
        }
    });
}

function formatMassResultMessage(result) {
    if (!result) return t('common.success') || 'Done';
    const parts = [];
    if (typeof result.created === 'number') parts.push(`${t('locations.massAlarm.created') || 'Created'}: ${result.created}`);
    if (typeof result.removed === 'number') parts.push(`${t('locations.massAlarm.removed') || 'Removed'}: ${result.removed}`);
    if (typeof result.updated === 'number') parts.push(`${t('locations.massAlarm.updated') || 'Updated'}: ${result.updated}`);
    if (typeof result.skipped === 'number' && result.skipped > 0) parts.push(`${t('locations.massAlarm.skipped') || 'Skipped'}: ${result.skipped}`);
    if (typeof result.incompatible === 'number' && result.incompatible > 0) parts.push(`${t('locations.massAlarm.incompatible') || 'Incompatible'}: ${result.incompatible}`);
    const sensors = typeof result.sensors === 'number' ? result.sensors : null;
    const locations = typeof result.locations === 'number' ? result.locations : null;
    let suffix = '';
    if (locations !== null && sensors !== null) {
        suffix = ` (${locations} ${t('locations.massAlarm.locations') || 'location(s)'}, ${sensors} ${t('locations.massAlarm.sensors') || 'sensor(s)'})`;
    }
    return parts.join(' · ') + suffix;
}

async function callBulkEndpoint(path, body) {
    const response = await fetch(`${API_BASE_URL}/alarm-templates/${path}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.token}`
        },
        body: JSON.stringify(body)
    });
    if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
            const err = await response.json();
            detail = err.detail || detail;
        } catch (_) { /* ignore */ }
        throw new Error(detail);
    }
    return response.json();
}

async function fetchSensorTypesForLocations(locationIds) {
    try {
        const response = await fetch(`${API_BASE_URL}/alarm-templates/assignments/sensor-types-for-locations`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${auth.token}`
            },
            body: JSON.stringify({ location_ids: locationIds })
        });
        if (!response.ok) throw new Error('Failed to load sensor types');
        return await response.json();
    } catch (e) {
        console.error('Error fetching sensor types:', e);
        return { sensor_types: [], sensors: 0 };
    }
}

function showMassAssignAlarmModal() {
    openMassAlarmModal({
        title: t('locations.massAlarm.assignTitle') || 'Assign alarm to locations',
        description: t('locations.massAlarm.assignDescription') || 'Assign the selected alarm template to every sensor across {count} location(s).',
        templateRequired: true,
        allowAll: false,
        filterTemplatesBySensorTypes: true,
        confirmLabel: t('locations.massAlarm.assignConfirm') || 'Assign',
        confirmClass: 'bg-green-600 hover:bg-green-700',
        action: ({ locationIds, templateId }) => callBulkEndpoint('assignments/bulk-assign', {
            location_ids: locationIds,
            template_id: templateId,
            enabled: true
        })
    });
}

function showMassRemoveAlarmModal() {
    openMassAlarmModal({
        title: t('locations.massAlarm.removeTitle') || 'Remove alarms from locations',
        description: t('locations.massAlarm.removeDescription') || 'Remove alarm assignments from every sensor across {count} location(s).',
        templateRequired: false,
        allowAll: true,
        confirmLabel: t('locations.massAlarm.removeConfirm') || 'Remove',
        confirmClass: 'bg-red-600 hover:bg-red-700',
        action: ({ locationIds, templateId }) => callBulkEndpoint('assignments/bulk-remove', {
            location_ids: locationIds,
            template_id: templateId
        })
    });
}

function showMassSuspendAlarmModal() {
    const extra = `
        <div>
            <label for="mass-alarm-days" class="block text-sm font-medium text-gray-700 mb-1">
                ${t('locations.massAlarm.suspendDays') || 'Suspend for (days)'} *
            </label>
            <input type="number" id="mass-alarm-days" min="1" max="365" step="1" value="7"
                   class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
        </div>
    `;
    openMassAlarmModal({
        title: t('locations.massAlarm.suspendTitle') || 'Suspend alarms for locations',
        description: t('locations.massAlarm.suspendDescription') || 'Temporarily suspend alarm assignments across {count} location(s).',
        templateRequired: false,
        allowAll: true,
        extraFieldsHtml: extra,
        confirmLabel: t('locations.massAlarm.suspendConfirm') || 'Suspend',
        confirmClass: 'bg-yellow-600 hover:bg-yellow-700',
        action: ({ locationIds, templateId }) => {
            const daysInput = document.getElementById('mass-alarm-days');
            const days = parseInt(daysInput && daysInput.value, 10);
            if (!days || days < 1 || days > 365) {
                throw new Error(t('locations.massAlarm.invalidDays') || 'Enter days between 1 and 365');
            }
            return callBulkEndpoint('assignments/bulk-suspend', {
                location_ids: locationIds,
                template_id: templateId,
                days
            });
        }
    });
}

function showMassResumeAlarmModal() {
    openMassAlarmModal({
        title: t('locations.massAlarm.resumeTitle') || 'Resume alarms for locations',
        description: t('locations.massAlarm.resumeDescription') || 'Clear suspension on alarm assignments across {count} location(s).',
        templateRequired: false,
        allowAll: true,
        confirmLabel: t('locations.massAlarm.resumeConfirm') || 'Resume',
        confirmClass: 'bg-blue-600 hover:bg-blue-700',
        action: ({ locationIds, templateId }) => callBulkEndpoint('assignments/bulk-resume', {
            location_ids: locationIds,
            template_id: templateId
        })
    });
}

// Re-export location edit functions
export { editLocation, handleUpdateLocation, closeEditLocationModal, toggleLocationAccountTreeItem, selectLocationAccount, geocodeLocationAddress };

// Make functions globally accessible for onclick handlers
if (typeof window !== 'undefined') {
    window.loadLocationsData = loadLocationsData;
    window.filterLocations = filterLocations;
    window.setLocationsSort = setLocationsSort;
    window.editLocation = editLocation;
    window.handleUpdateLocation = handleUpdateLocation;
    window.closeEditLocationModal = closeEditLocationModal;
    window.toggleLocationAccountTreeItem = toggleLocationAccountTreeItem;
    window.selectLocationAccount = selectLocationAccount;
    window.geocodeLocationAddress = geocodeLocationAddress;
    window.toggleLocationsAccountFilterExpanded = toggleLocationsAccountFilterExpanded;
    window.applyLocationsAccountFilter = applyLocationsAccountFilter;
    window.clearLocationsAccountFilter = clearLocationsAccountFilter;
    window.clearLocationsFilters = clearLocationsFilters;
    window.toggleLocationsAccountFilterTreeItem = toggleLocationsAccountFilterTreeItem;
    window.toggleLocationSelection = toggleLocationSelection;
    window.toggleSelectAllLocations = toggleSelectAllLocations;
    window.clearLocationSelection = clearLocationSelection;
    window.showMassAssignAlarmModal = showMassAssignAlarmModal;
    window.showMassRemoveAlarmModal = showMassRemoveAlarmModal;
    window.showMassSuspendAlarmModal = showMassSuspendAlarmModal;
    window.showMassResumeAlarmModal = showMassResumeAlarmModal;
    window.closeMassAlarmModal = closeMassAlarmModal;
}

export default {
    showLocations,
    loadLocationsData,
    filterLocations,
    clearLocationsFilters,
    applyLocationsAccountFilter,
    clearLocationsAccountFilter,
    toggleLocationsAccountFilterExpanded,
    toggleLocationsAccountFilterTreeItem
};
